import { useEffect, useCallback } from "react";
import { debugLog, debugError } from "../common/utils/debug";
import { audioService } from "../services/audio.service";
import { speechToTextSileroService } from "../services/speech-to-text-silero.service";
import { signalRService } from "../services/signalr.service";
import { useMute } from "../context/mute";
import { usePause } from "../context/pause";

interface UseAudioMuteOptions {
  isConnected: boolean;
  avatarReady: boolean;
  isDisconnecting?: boolean;
  isSpeaking?: boolean;
}

/**
 * Hook that abstracts all mute/unmute audio functionality
 * Handles microphone control, speech-to-text, and streaming recognition
 */
export const useAudioMute = ({
  isConnected,
  avatarReady,
  isDisconnecting = false,
  isSpeaking = false,
}: UseAudioMuteOptions) => {
  const { isMuted, toggleMute } = useMute();
  const { isPaused } = usePause();

  const canToggle = isConnected && avatarReady && !isPaused && !isDisconnecting && !isSpeaking;

  // Pre-initialize audio pipeline when avatar becomes ready
  useEffect(() => {
    if (isConnected && avatarReady) {
      debugLog('🎬 [useAudioMute] Avatar ready - pre-initializing audio pipeline...');
      speechToTextSileroService.initialize()
        .then(() => debugLog('✅ [useAudioMute] Audio pipeline pre-initialized'))
        .catch(err => debugError('❌ [useAudioMute] Failed to pre-initialize:', err));
    }
  }, [isConnected, avatarReady]);

  // Auto-start/stop microphone based on mute state and connection status
  // OPTIMIZATION: Keep microphone WARM during avatar speech for instant user response
  useEffect(() => {
    debugLog('🔧 [useAudioMute] Effect triggered:', { isConnected, avatarReady, isMuted, isSpeaking });
    
    const manageMicrophone = async () => {
      // CASE 1: Avatar is speaking - KEEP MICROPHONE WARM (recording but muted)
      // This is the HIGHEST PRIORITY to ensure instant response when avatar finishes
      // EXCEPTION: If user is ALREADY speaking, DON'T mute them - let them finish!
      if (isConnected && avatarReady && isSpeaking) {
        const isUserSpeaking = speechToTextSileroService.isUserSpeaking();
        
        if (isUserSpeaking) {
          debugLog('🚫 [useAudioMute] Avatar speaking BUT user is ALREADY speaking - NOT muting user!');
          debugLog('👂 [useAudioMute] User will finish their sentence, then avatar speech will be queued');
          // Don't mute - let the user finish their current sentence
          // The avatar's speech will be queued and played after user stops
          return;
        }
        
        debugLog('🔥 [useAudioMute] Avatar speaking - keeping microphone WARM (muted recording)');
        try {
          // Start recording to warm up getUserMedia and audio pipeline
          await speechToTextSileroService.startRecording();
          // BUT mute VAD so no audio chunks are sent to backend
          speechToTextSileroService.setMuted(true);
          try {
            await audioService.startRecording();
          } catch (audioError) {}
          debugLog('✅ [useAudioMute] Microphone WARM and ready - will unmute instantly when avatar finishes');
        } catch (error: any) {
          debugError('❌ [useAudioMute] Failed to warm microphone:', error);
        }
      }
      // CASE 2: User wants to speak (not muted, not avatar speaking)
      else if (isConnected && avatarReady && !isMuted && !isSpeaking) {
        debugLog('🎤 [useAudioMute] Starting microphone and unmuting VAD...');
        try {
          // Unmute VAD service to allow audio transmission
          speechToTextSileroService.setMuted(false);
          await speechToTextSileroService.startRecording();
          try {
            await audioService.startRecording();
          } catch (audioError) {}
        } catch (error: any) {
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error";
          window.dispatchEvent(
            new CustomEvent("microphone_error", {
              detail: {
                error: errorMessage,
                originalError: error,
              },
            })
          );
        }
      }
      // CASE 3: Stop microphone (disconnected, not ready, or user muted and not speaking)
      else if (!isConnected || !avatarReady || (isMuted && !isSpeaking)) {
        debugLog('🛑 [useAudioMute] Stopping microphone, reason:', { 
          notConnected: !isConnected, 
          notReady: !avatarReady, 
          muted: isMuted,
          speaking: isSpeaking
        });
        try {
          // If just muted (but connected and ready), keep recording but mute transmission
          if (isMuted && isConnected && avatarReady) {
            debugLog('🔇 [useAudioMute] Muting VAD service (keeping recording alive)');
            speechToTextSileroService.setMuted(true);
            // DON'T call stopRecording() - keep the session warm!
          } else {
            // Fully disconnect (not connected or not ready)
            debugLog('🛑 [useAudioMute] Fully stopping microphone (disconnected or not ready)');
            speechToTextSileroService.stopRecording();
            audioService.stopRecording();
          }
        } catch (error) {}
      }
    };

    manageMicrophone();
  }, [isConnected, avatarReady, isMuted, isSpeaking]);

  useEffect(() => {
    if (isMuted) {
      audioService.mute();
    }
  }, [isMuted]);

  const handleToggleMute = useCallback(() => {
    if (!canToggle) {
      return;
    }
    toggleMute();
  }, [canToggle, toggleMute]);

  return {
    isMuted,
    toggleMute: handleToggleMute,
    canToggle,
  };
};
